// @dynamic-i18n-keys
// Dynamic t() at line 88 (submit | update):
//   deviationSubmit.form.button.submit
//   deviationSubmit.form.button.update
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { BiData, SubmitContentBiEvent } from '@wix/da-bi/pkg/events';
import { getDeviationResourceType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import CoreSymbol from '@wix/da-ds/pkg/Indicators/CoreSymbol';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import BrandedSplitButton from '@wix/da-ds/pkg/Dropdown/Preset/BrandedSplitButton';
import { SubmitFormContext } from '../../../context';
import { useSubmitFormStatus } from '../../../hooks/useSubmitFormStatus';

export interface Props {
  scheduleTimestamp?: string;
  onScheduleClick: (deviation: PapiDeviation) => void;
  requestSubmit: () => void;
  requestSchedule?: () => void;
}

const SubmitButton: React.FC<Props> = ({
  scheduleTimestamp,
  onScheduleClick,
  requestSubmit,
  requestSchedule,
}) => {
  const { t } = useTranslation();
  const { isEdit, initResponse } = useContext(SubmitFormContext)!;
  const { isBusy } = useSubmitFormStatus();
  const deviation = useMemo(() => initResponse?.deviation, [initResponse]);
  const isAppDeviation = !!initResponse?.app;
  const isOriginalsDelivery = deviation?.isOriginalsDelivery;
  const needsPreview =
    initResponse?.requiresPreview && !initResponse.hasCustomPreview;

  if (scheduleTimestamp && requestSchedule) {
    return (
      <BrandedButton
        size="large"
        variant="brand-green"
        onClick={requestSchedule}
        loading={isBusy}
      >
        {t('deviationSubmit.form.button.schedule')}
      </BrandedButton>
    );
  }

  if (!deviation?.isPublished && !isAppDeviation && !isOriginalsDelivery) {
    return (
      <BrandedSplitButton
        menuScrollable={false}
        primaryButton={{
          biData: BiData<SubmitContentBiEvent>({
            evid: 11,
            itemid: deviation?.deviationId,
            typeid: getDeviationResourceType(deviation),
          }),
          onClick: requestSubmit,
        }}
        dropdownItems={[
          {
            label: t('deviationSubmit.form.button.schedule'),
            suffix: <CoreSymbol size={IconSize.SMALL} />,
            onClick: needsPreview
              ? requestSchedule // this will trigger validation
              : () => deviation && onScheduleClick(deviation),
          },
        ]}
        loading={isBusy}
      >
        {t('deviationSubmit.form.button.submit')}
      </BrandedSplitButton>
    );
  }

  return (
    <BrandedButton
      size="large"
      variant="brand-green"
      onClick={requestSubmit}
      loading={isBusy}
      biData={BiData<SubmitContentBiEvent>({
        evid: 11,
        itemid: deviation?.deviationId,
        typeid: getDeviationResourceType(deviation),
      })}
    >
      {t(
        `deviationSubmit.form.button.${
          isAppDeviation || !isEdit ? 'submit' : 'update'
        }`
      )}
    </BrandedButton>
  );
};
SubmitButton.displayName = 'SubmitButton';

export default SubmitButton;
